import Vuex from "vuex";
import axios from "axios";
import { describeError, resourceError } from "../misc/request-errors.js";
import { STATUS_PENDING, STATUS_SUCCESS, STATUS_FAILED } from "../misc/constants.js";

function newsletterDefaults() {
  return { email: null, newsletterOptions: [], subscribeNewsletter: false };
}

export default {
  data() {
    return {
      loadStatus: STATUS_PENDING,
      loadError: null,
      newsletterCheckError: null,
      status: {},
      errors: {},
      review: {
        selectedBooking: 0,
        email: "",
        trainerSubject: null,
        trainerMethod: null,
        trainerComment: "",
        seminarRating: null,
        seminarComment: "",
        gfuRating: null,
        gfuComment: "",
        furtherInterest: "",
        anonymityLevelName: 1,
        anonymityLevelCompany: 1,
        suggestions: "",
        newsletter: newsletterDefaults(),
      },
    };
  },
  computed: {
    ...Vuex.mapGetters(["seminarDate", "booking", "isSubscribed"]),
    bookingHash() { return this.$route.params.bookingHash; },
    seminarDateHash() { return this.$route.params.seminarDateHash; },
    selectedBookingId() {
      return this.bookingHash ? this.booking?.id : this.review.selectedBooking.id;
    },
    submitting() {
      return Object.values(this.status).includes(STATUS_PENDING);
    },
  },
  created() {
    this.loadController = null;
    this.newsletterController = null;
  },
  mounted() {
    this.loadForm();
  },
  beforeDestroy() {
    this.loadController?.abort();
    this.newsletterController?.abort();
  },
  methods: {
    ...Vuex.mapActions(["fetchSeminarDate", "fetchBooking", "createReview", "checkMail", "createAccount"]),
    async loadForm() {
      if (this.loadController) return;
      this.loadController = new AbortController();
      const signal = this.loadController.signal;
      this.loadStatus = STATUS_PENDING;
      this.loadError = null;
      this.$store.commit("setBooking", null);
      this.$store.commit("setSeminarDate", {});
      this.$store.commit("setIsSubscribed", null);
      try {
        if (this.bookingHash) await this.fetchBooking({ hash: this.bookingHash, signal });
        else if (this.seminarDateHash) await this.fetchSeminarDate({ hash: this.seminarDateHash, signal });
        else throw resourceError("RESOURCE_NOT_FOUND");
        this.loadStatus = STATUS_SUCCESS;
        if (this.bookingHash) this.checkNewsletter();
      } catch (error) {
        if (axios.isCancel(error)) return;
        this.loadError = describeError(error, "load", navigator.onLine);
        this.loadStatus = STATUS_FAILED;
      } finally {
        this.loadController = null;
      }
    },
    async checkNewsletter() {
      this.newsletterController?.abort();
      this.newsletterCheckError = null;
      this.$store.commit("setIsSubscribed", null);
      if (!this.selectedBookingId) return;
      const controller = new AbortController();
      this.newsletterController = controller;
      try {
        await this.checkMail({ bookingId: this.selectedBookingId, signal: controller.signal });
      } catch (error) {
        if (axios.isCancel(error)) return;
        this.newsletterCheckError = describeError(error, "newsletterCheck", navigator.onLine);
      } finally {
        if (this.newsletterController === controller) this.newsletterController = null;
      }
    },
    participantChanged() {
      this.review.newsletter = newsletterDefaults();
      this.status = {};
      this.errors = {};
      this.checkNewsletter();
    },
    checkThenSubmit(observer) {
      if (this.submitting || this.loadStatus !== STATUS_SUCCESS) return;
      observer.handleSubmit(this.saveReview).then(() => {
        if (observer.invalid) this.scrollToError(observer);
      });
    },
    scrollToError(observer) {
      const field = Object.values(observer.fields).find(field => field.invalid);
      this.$refs.ratingForm.refs[field.name].$el.scrollIntoView({ behavior: "smooth" });
    },
    async submitPart(operation, action, payload) {
      this.status = { ...this.status, [operation]: STATUS_PENDING };
      this.errors = { ...this.errors, [operation]: null };
      try {
        await action(payload);
        this.status = { ...this.status, [operation]: STATUS_SUCCESS };
      } catch (error) {
        this.errors = { ...this.errors, [operation]: describeError(error, operation, navigator.onLine) };
        this.status = { ...this.status, [operation]: STATUS_FAILED };
      }
    },
    async saveReview() {
      if (this.submitting || this.loadStatus !== STATUS_SUCCESS) return;
      const requests = [this.submitPart("review", this.createReview, {
        ...this.review,
        booking_id: this.selectedBookingId,
        allowEkomi: 1,
      })];
      if (this.review.newsletter.subscribeNewsletter
        && this.status.newsletter !== STATUS_SUCCESS && !this.errors.newsletter?.uncertain) {
        requests.push(this.submitPart("newsletter", this.createAccount, {
          ...this.review.newsletter,
          bookingId: this.selectedBookingId,
        }));
      }
      await Promise.all(requests);
    },
    resumeForm() {
      this.status = { ...this.status, review: null };
    },
  },
};
